import React, { useState, useEffect, useRef } from 'react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import './CookieSettingsModal.css';

const ChevronDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CookieSettingsModal = ({ onClose }) => {
  const { consent, updateConsent, rejectAll } = useCookieConsent();
  const [analytics, setAnalytics] = useState(consent?.analytics || false);
  const [marketing, setMarketing] = useState(consent?.marketing || false);
  const [showInventory, setShowInventory] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }

      if (e.key === 'Tab') {
        const focusable = modalRef.current?.querySelectorAll(
          'button, input, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable && focusable.length) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSave = () => {
    updateConsent({ analytics, marketing });
  };

  const handleRejectAll = () => {
    rejectAll();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content cookie-settings-modal" ref={modalRef} role="dialog" aria-label="Cookie preferences" aria-modal="true">
        <button className="close-button" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="cookie-settings-header">
          <h2>Cookie Preferences</h2>
          <p>
            Manage your cookie preferences below. Strictly necessary cookies
            cannot be disabled as they are required for the website to function.
          </p>
        </div>

        <div className="cookie-settings-body">
          {/* Strictly Necessary */}
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <p className="cookie-category-name">Strictly Necessary</p>
                <p className="cookie-category-desc">
                  Required for the website to function properly.
                </p>
                <p className="cookie-category-details">
                  Consent preferences, page variant for consistent rendering.
                </p>
              </div>
              <span className="cookie-always-on">Always On</span>
            </div>
          </div>

          {/* Analytics */}
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <p className="cookie-category-name">Analytics</p>
                <p className="cookie-category-desc">
                  Help us understand how visitors interact with our website.
                </p>
                <p className="cookie-category-details">
                  CTA click tracking, session analytics.
                </p>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
          </div>

          {/* Marketing */}
          <div className="cookie-category">
            <div className="cookie-category-header">
              <div className="cookie-category-info">
                <p className="cookie-category-name">Marketing</p>
                <p className="cookie-category-desc">
                  Used to deliver relevant ads and measure ad campaigns.
                </p>
                <p className="cookie-category-details">
                  Google Ads conversion tracking, Tidio live chat.
                </p>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
                <span className="cookie-toggle-slider" />
              </label>
            </div>
          </div>

          {/* Cookie Inventory */}
          <button
            className={`cookie-inventory-toggle ${showInventory ? 'expanded' : ''}`}
            onClick={() => setShowInventory(!showInventory)}
          >
            View full cookie inventory <ChevronDown />
          </button>

          {showInventory && (
            <>
              <table className="cookie-inventory-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                    <th>Category</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>qate_cookie_consent</td>
                    <td>Qate AI</td>
                    <td>Stores your cookie preferences</td>
                    <td>1 year</td>
                    <td>Necessary</td>
                  </tr>
                  <tr>
                    <td>hp_variant</td>
                    <td>Qate AI</td>
                    <td>Ensures consistent page rendering</td>
                    <td>1 year</td>
                    <td>Necessary</td>
                  </tr>
                  <tr>
                    <td>sessionStorage (analytics)</td>
                    <td>Qate AI</td>
                    <td>CTA click tracking, session ID</td>
                    <td>Session</td>
                    <td>Analytics</td>
                  </tr>
                  <tr>
                    <td>_gcl_*</td>
                    <td>Google</td>
                    <td>Ad conversion tracking</td>
                    <td>90 days</td>
                    <td>Marketing</td>
                  </tr>
                  <tr>
                    <td>Tidio cookies</td>
                    <td>Tidio</td>
                    <td>Live chat widget &amp; visitor data</td>
                    <td>Session / Persistent</td>
                    <td>Marketing</td>
                  </tr>
                </tbody>
              </table>

              <p className="cookie-inventory-section-title">
                App (app.qate.ai) — all strictly necessary
              </p>
              <table className="cookie-inventory-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Purpose</th>
                    <th>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>authToken</td>
                    <td>Authentication (HTTP-only)</td>
                    <td>24 hours</td>
                  </tr>
                  <tr>
                    <td>refreshToken</td>
                    <td>Session refresh (HTTP-only)</td>
                    <td>30 days</td>
                  </tr>
                  <tr>
                    <td>testmind-theme</td>
                    <td>Theme preference</td>
                    <td>Permanent</td>
                  </tr>
                  <tr>
                    <td>qate-last-selected-application</td>
                    <td>Last selected application</td>
                    <td>Permanent</td>
                  </tr>
                  <tr>
                    <td>auth-storage</td>
                    <td>Authentication state</td>
                    <td>Permanent</td>
                  </tr>
                  <tr>
                    <td>OAuth state tokens</td>
                    <td>OAuth CSRF protection</td>
                    <td>Session</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className="cookie-settings-footer">
          <button className="cookie-btn cookie-btn-reject" onClick={handleRejectAll}>
            Reject All
          </button>
          <button className="cookie-btn cookie-btn-accept" onClick={handleSave}>
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
